import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  topicService, errorService, courseService, dashboardService, flashcardProgressService, progressService,
} from '../services/api';
import {
  ArrowRight, BellRing, BookOpen, ChevronRight, CirclePlay, CircleX, FileText, Flame, Layers, Library, NotebookPen, Play,
  Plus, Route, Search, Target,
} from 'lucide-react';
import { useCourse } from '../contexts/CourseContext';
import { useAuth } from '../contexts/AuthContext';
import {
  AppShell, Button, Chip, EmptyState, Field, Loader, Modal, PageHeader, ScoreCard, ScoreHero, SectionTitle,
} from '../components/ui';
import './StudyHome.css';

function StudyHome() {
  const navigate = useNavigate();
  const { courseSlug } = useParams();
  const { currentCourse, selectCourse, getPhases } = useCourse();
  const [topics, setTopics] = useState([]);
  const [errorCount, setErrorCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAddTopic, setShowAddTopic] = useState(false);
  const [newTopic, setNewTopic] = useState({ name: '', description: '', fase: 1 });
  const [selectedFase, setSelectedFase] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [dueFlashcards, setDueFlashcards] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [lessonProgress, setLessonProgress] = useState({});
  const [stickyTop, setStickyTop] = useState(0);
  const { isAdmin } = useAuth();

  // Get phases from course context
  const FASES = getPhases();

  useEffect(() => {
    if (courseSlug) {
      selectCourse(courseSlug);
    }
  }, [courseSlug, selectCourse]);

  useEffect(() => {
    if (courseSlug) {
      loadData();
    }
  }, [courseSlug]);

  // Os chips de fase grudam logo abaixo da barra do topo (altura varia com o aviso de demonstração)
  useEffect(() => {
    const bar = document.querySelector('.ss-topbar');
    if (!bar) return undefined;
    const update = () => setStickyTop(bar.offsetHeight);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [loading]);

  const loadData = async () => {
    try {
      const [topicsData, errorsCount] = await Promise.all([
        courseService.getTopicsByCourse(courseSlug, true),
        errorService.countErrors(null, courseSlug),
      ]);
      setTopics(topicsData);
      setErrorCount(errorsCount);
      loadDashboard();
    } catch (error) {
      console.error('Error loading data:', error);
      try {
        const [topicsData, errorsCount] = await Promise.all([
          topicService.getAllTopics(true, courseSlug),
          errorService.countErrors(null, courseSlug),
        ]);
        setTopics(topicsData);
        setErrorCount(errorsCount);
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadDashboard = async () => {
    try {
      const [dashData, dueCount, progressData] = await Promise.all([
        dashboardService.getDashboard(courseSlug),
        flashcardProgressService.countDueCards(courseSlug),
        progressService.getProgressByCourse(courseSlug).catch(() => []),
      ]);
      setDashboard(dashData);
      setDueFlashcards(dueCount);
      const progressMap = {};
      progressData.forEach(p => { progressMap[p.lessonId] = p.completed; });
      setLessonProgress(progressMap);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(buildPath(`/buscar?q=${encodeURIComponent(searchQuery)}`));
    }
  };

  const handleAddTopic = async () => {
    if (!newTopic.name.trim()) return;
    try {
      await courseService.createTopicInCourse(courseSlug, newTopic);
      setNewTopic({ name: '', description: '', fase: 1 });
      setShowAddTopic(false);
      loadData();
    } catch (error) {
      console.error('Error creating topic:', error);
      // Fallback to generic topic creation
      try {
        await topicService.createTopic({ ...newTopic, courseSlug });
        setNewTopic({ name: '', description: '', fase: 1 });
        setShowAddTopic(false);
        loadData();
      } catch (fallbackError) {
        console.error('Fallback creation failed:', fallbackError);
      }
    }
  };

  // Agrupa topicos por fase para exibicao
  const topicsByFase = FASES.map(fase => ({
    ...fase,
    topics: topics.filter(t => t.fase === fase.num)
  }));

  // Conta aulas por fase
  const getLessonCount = (faseNum) => {
    return topics
      .filter(t => t.fase === faseNum)
      .reduce((acc, t) => acc + (t.lessons?.length || 0), 0);
  };

  // Build navigation paths with course slug
  const buildPath = (path) => `/curso/${courseSlug}${path}`;

  const totalLessons = topics.reduce((acc, t) => acc + (t.lessons?.length || 0), 0);
  const progressPct = dashboard?.totalLessons
    ? Math.round((dashboard.completedLessons / dashboard.totalLessons) * 100)
    : 0;
  const visibleFases = selectedFase ? FASES.filter(f => f.num === selectedFase) : FASES;
  const activity = dashboard?.activity;
  // Só o simulado em andamento vira aviso; sequência, flashcards e erros já aparecem na página
  const examReminder = dashboard?.reminders?.find(r => r.type === 'exam');

  // "Continuar de onde parei": última aula vista ou, sem histórico, o primeiro tópico do curso
  const continueInfo = (() => {
    const last = dashboard?.lastLesson;
    const topic = last ? topics.find(t => t.id === last.topicId) : topics.find(t => t.lessons?.length) || topics[0];
    if (!topic && !last) return null;
    const lessons = topic?.lessons || [];
    const done = lessons.filter(l => lessonProgress[l.id]).length;
    const fase = topic ? `Fase ${String(topic.fase).padStart(2, '0')} · ` : '';
    if (last) {
      const index = lessons.findIndex(l => l.id === last.lessonId);
      return {
        label: 'Continuar de onde parei',
        title: last.lessonName,
        meta: `${fase}${last.topicName}${index >= 0 ? ` · aula ${index + 1} de ${lessons.length}` : ''}${last.completed ? ' · concluída' : ''}`,
        done,
        total: lessons.length,
        cta: 'Continuar aula',
        path: buildPath(`/estudo/${last.topicId}/aula/${last.lessonId}`),
      };
    }
    return {
      label: 'Comece por aqui',
      title: topic.name,
      meta: `${fase}${lessons.length} ${lessons.length === 1 ? 'aula' : 'aulas'}`,
      done,
      total: lessons.length,
      cta: 'Começar tópico',
      path: buildPath(`/estudo/${topic.id}`),
    };
  })();

  if (loading) {
    return (
      <AppShell section="Modo Estudo" courseName={currentCourse?.name}>
        <Loader />
      </AppShell>
    );
  }

  return (
    <AppShell section="Modo Estudo" sectionNum={1} courseName={currentCourse?.name}>
      <div className="ss-page">
        <PageHeader
          icon={BookOpen}
          eyebrow={currentCourse?.name || 'Carregando...'}
          title="Modo Estudo"
          subtitle="Escolha uma fase e um tópico para continuar estudando."
          actions={isAdmin && (
            <Button variant="outline" icon={Plus} onClick={() => setShowAddTopic(true)} data-tour="novo-topico">
              Novo tópico
            </Button>
          )}
        />

        {examReminder && (
          <div className="study-alert" data-tour="aviso-simulado">
            <BellRing size={15} aria-hidden="true" />
            <span>{examReminder.message}</span>
            {examReminder.refId && (
              <Button size="sm" variant="accent" onClick={() => navigate(buildPath(`/exam/${examReminder.refId}`))}>
                {examReminder.action || 'Retomar'}
              </Button>
            )}
          </div>
        )}

        {activity && (
          <div className="study-streak" data-tour="sequencia">
            <Flame size={15} aria-hidden="true" />
            <span>
              {activity.streak > 0 ? (
                <><strong>{activity.streak} {activity.streak === 1 ? 'dia seguido' : 'dias seguidos'}</strong> · {activity.studiedToday ? 'você já estudou hoje' : 'estude hoje para manter a sequência'}</>
              ) : (
                <><strong>Nenhuma sequência ativa</strong> · estude hoje para começar uma</>
              )}
            </span>
            <button type="button" className="study-streak__link" onClick={() => navigate(buildPath('#sua-semana'))}>
              Ver minha semana <ArrowRight size={13} aria-hidden="true" />
            </button>
          </div>
        )}

        <div className={`study-today ${continueInfo ? '' : 'study-today--solo'}`}>
          {continueInfo ? (
            <div className="study-continue" data-tour="continuar">
              <div className="study-continue__text">
                <span className="study-continue__label">{continueInfo.label}</span>
                <strong>{continueInfo.title}</strong>
                <span>{continueInfo.meta}</span>
              </div>
              {continueInfo.total > 0 && (
                <div className="study-continue__progress">
                  <span className="study-continue__bar" aria-hidden="true">
                    <span style={{ width: `${(continueInfo.done / continueInfo.total) * 100}%` }} />
                  </span>
                  <span>{continueInfo.done}/{continueInfo.total} aulas do tópico</span>
                </div>
              )}
              <button type="button" className="study-continue__cta" onClick={() => navigate(continueInfo.path)}>
                <span aria-hidden="true"><Play size={13} /></span>
                {continueInfo.cta}
              </button>
            </div>
          ) : null}

          <ScoreCard
            className="study-score"
            tour="progresso"
            top={(
              <ScoreHero
                eyebrow="Progresso geral"
                caption={dashboard ? `${dashboard.completedLessons}/${dashboard.totalLessons} aulas concluídas` : 'Sem dados de progresso ainda'}
                value={`${progressPct}%`}
                percent={progressPct}
              />
            )}
            items={[
              { icon: Library, value: topics.length, label: 'Tópicos' },
              { icon: CirclePlay, value: totalLessons, label: 'Aulas' },
              { icon: Target, value: dashboard?.completedExams || 0, label: 'Simulados', onClick: () => navigate(buildPath('/simulados')) },
              { icon: CircleX, value: errorCount, label: 'Erros', tone: errorCount > 0 ? 'danger' : 'navy', onClick: () => navigate(buildPath('/erros')) },
            ]}
          />
        </div>

        <div className="study-actions" data-tour="acoes">
          {[
            { icon: Target, title: 'Novo simulado', desc: '30 questões · 60 min', path: '/config' },
            { icon: Layers, title: 'Revisar flashcards', desc: `${dueFlashcards} para hoje`, path: '/flashcards' },
            { icon: NotebookPen, title: 'Caderno de erros', desc: `${errorCount} para revisar`, path: '/erros' },
          ].map(a => (
            <button key={a.path} type="button" className="study-action" onClick={() => navigate(buildPath(a.path))}>
              <span className="study-action__icon"><a.icon size={18} aria-hidden="true" /></span>
              <span className="study-action__text">
                <strong>{a.title}</strong>
                <span>{a.desc}</span>
              </span>
            </button>
          ))}
        </div>

        <section className="ss-section">
          <div className="study-phases__head" data-tour="fases">
            <div>
              <SectionTitle icon={Route}>Fases do curso</SectionTitle>
              <span className="study-phases__meta">
                {FASES.length} fases · {topics.length} tópicos · {totalLessons} aulas
              </span>
            </div>
            <form onSubmit={handleSearch} className="study-search" role="search">
              <Search size={15} aria-hidden="true" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar tópicos, aulas e questões…"
                aria-label="Buscar"
              />
            </form>
          </div>
          <div className="study-filter" style={{ top: stickyTop }}>
            <Chip active={selectedFase === null} onClick={() => setSelectedFase(null)} count={topics.length}>
              Todas
            </Chip>
            {FASES.map(fase => (
              <Chip
                key={fase.num}
                active={selectedFase === fase.num}
                color={fase.color}
                onClick={() => setSelectedFase(fase.num)}
                count={topicsByFase[fase.num - 1]?.topics.length || 0}
              >
                Fase {fase.num}
              </Chip>
            ))}
          </div>

          <div className="phase-list">
            {visibleFases.map(fase => {
              const faseTopics = topicsByFase[fase.num - 1]?.topics || [];
              if (faseTopics.length === 0 && selectedFase === null) return null;
              const faseLessons = faseTopics.flatMap(t => t.lessons || []);
              const fasePct = faseLessons.length
                ? Math.round((faseLessons.filter(l => lessonProgress[l.id]).length / faseLessons.length) * 100)
                : 0;

              return (
                <section key={fase.num} className="phase-block">
                  <div className="phase-block__head">
                    <span className="phase-block__num" style={{ background: fase.color }}>
                      {String(fase.num).padStart(2, '0')}
                    </span>
                    <div className="phase-block__title">
                      <h3>{fase.name}</h3>
                      <span className="phase-block__meta">
                        {faseTopics.length} {faseTopics.length === 1 ? 'tópico' : 'tópicos'} · {getLessonCount(fase.num)} aulas
                      </span>
                    </div>
                    <span className="phase-block__pct">{fasePct}%</span>
                    <span className="phase-block__bar" aria-hidden="true">
                      <span style={{ width: `${fasePct}%`, background: fase.color }} />
                    </span>
                  </div>

                  {faseTopics.length === 0 ? (
                    <EmptyState
                      file={`fase-${fase.num}.xml`}
                      title="Nenhum tópico nesta fase"
                      description="Crie o primeiro tópico para começar a organizar o conteúdo."
                      action={<Button icon={Plus} onClick={() => setShowAddTopic(true)}>Novo tópico</Button>}
                    />
                  ) : (
                    <div className="topic-grid">
                      {faseTopics.map(topic => (
                        <button
                          key={topic.id}
                          type="button"
                          className="topic-tile"
                          data-tour="topico-card"
                          onClick={() => navigate(buildPath(`/estudo/${topic.id}`))}
                        >
                          <span className="topic-tile__icon" style={{ background: fase.color }}>
                            <BookOpen size={16} aria-hidden="true" />
                          </span>
                          <span className="topic-tile__body">
                            <span className="topic-tile__name">{topic.name}</span>
                            <span className="topic-tile__tags">
                              <span className="topic-tile__tag">{topic.lessons?.length || 0} aulas</span>
                              {topic.pdfPath && <span className="topic-tile__tag"><FileText size={10} aria-hidden="true" /> PDF</span>}
                              {topic.onePage && <span className="topic-tile__tag topic-tile__tag--lime">One-Page</span>}
                            </span>
                          </span>
                          <ChevronRight size={15} className="topic-tile__go" aria-hidden="true" />
                        </button>
                      ))}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        </section>
      </div>

      <Modal
        open={showAddTopic}
        onClose={() => setShowAddTopic(false)}
        title="Novo tópico"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowAddTopic(false)}>Cancelar</Button>
            <Button onClick={handleAddTopic} disabled={!newTopic.name.trim()}>Criar tópico</Button>
          </>
        }
      >
        <Field label="Fase" required>
          <select
            value={newTopic.fase}
            onChange={(e) => setNewTopic({ ...newTopic, fase: parseInt(e.target.value) })}
          >
            {FASES.map(fase => (
              <option key={fase.num} value={fase.num}>
                Fase {fase.num} - {fase.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Nome do tópico" required>
          <input
            type="text"
            value={newTopic.name}
            onChange={(e) => setNewTopic({ ...newTopic, name: e.target.value })}
            placeholder="Ex: Modelagem de Dados"
            autoFocus
          />
        </Field>
        <Field label="Descrição">
          <textarea
            value={newTopic.description}
            onChange={(e) => setNewTopic({ ...newTopic, description: e.target.value })}
            placeholder="Breve descrição do tópico..."
            rows={3}
          />
        </Field>
      </Modal>
    </AppShell>
  );
}

export default StudyHome;
